import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Resets the discount input and notifies any UI that watches input events.
   * @param {HTMLInputElement} discountCode - The discount input.
   */
  #clearDiscountCode(discountCode) {
    discountCode.value = '';
    discountCode.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /**
   * Hides any currently visible discount errors.
   */
  #hideDiscountErrors() {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');
  }

  /**
   * Compares discount codes while ignoring user-entered whitespace and code casing.
   * @param {string | null | undefined} firstCode - The first discount code.
   * @param {string | null | undefined} secondCode - The second discount code.
   * @returns {boolean}
   */
  #discountCodesMatch(firstCode, secondCode) {
    return String(firstCode ?? '').trim().toLowerCase() === String(secondCode ?? '').trim().toLowerCase();
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value.trim();
    discountCode.value = discountCodeValue;
    discountCode.blur();

    if (!discountCodeValue) {
      this.#clearDiscountCode(discountCode);
      return;
    }

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.some((existingDiscount) => this.#discountCodesMatch(existingDiscount, discountCodeValue))) {
        this.#clearDiscountCode(discountCode);
        this.#closeDiscountDisclosure();
        return;
      }

      this.#hideDiscountErrors();

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error('Discount update failed');

      const data = await response.json();
      const appliedDiscounts = Array.isArray(data.discount_codes) ? data.discount_codes : [];
      const submittedDiscount = appliedDiscounts.find(
        (/** @type {{ code: string; applicable: boolean; }} */ discount) =>
          this.#discountCodesMatch(discount.code, discountCodeValue)
      );

      if (submittedDiscount?.applicable === false) {
        this.#handleDiscountError('discount_code');
        this.#closeDiscountDisclosure(1000);
        return;
      }

      const newHtml = data.sections?.[this.dataset.sectionId];
      if (!newHtml) throw new Error('Discount section missing');

      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodePills = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodePills)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        const submittedCodeInRenderedPills = codes.some((/** @type {string} */ code) =>
          this.#discountCodesMatch(code, discountCodeValue)
        );
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) =>
            existingDiscounts.some((existingDiscount) => this.#discountCodesMatch(existingDiscount, code))
          ) &&
          submittedDiscount?.applicable === true
        ) {
          this.#handleDiscountError('shipping');
          this.#closeDiscountDisclosure(1000);
          return;
        }

        if (!submittedCodeInRenderedPills && submittedDiscount?.applicable !== true) {
          this.#handleDiscountError('discount_code');
          this.#closeDiscountDisclosure(1000);
          return;
        }
      }

      this.#clearDiscountCode(discountCode);
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
      this.#closeDiscountDisclosure();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      this.#handleDiscountError('discount_code');
      this.#closeDiscountDisclosure(1000);
    } finally {
      if (this.#activeFetch === abortController) {
        this.#activeFetch = null;
      }
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      if (this.#activeFetch === abortController) {
        this.#activeFetch = null;
      }
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }

  /**
   * Closes the discount disclosure.
   * @param {number} delay - Delay in milliseconds before closing.
   */
  #closeDiscountDisclosure(delay = 0) {
    const sectionId = this.dataset.sectionId;

    const close = () => {
      document.querySelectorAll('cart-discount-component').forEach((component) => {
        if (!(component instanceof HTMLElement)) return;
        if (sectionId && component.dataset.sectionId !== sectionId) return;

        const disclosure = component.closest('details');
        if (disclosure instanceof HTMLDetailsElement) {
          disclosure.open = false;
        }
      });
    };

    if (delay > 0) {
      window.setTimeout(close, delay);
    } else {
      requestAnimationFrame(close);
    }
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
